import { ColumnDef } from '@tanstack/react-table';
import {
  Building2,
  Copy,
  Ellipsis,
  Eye,
  Info,
  Laptop,
  Pencil,
  Receipt,
  ShieldCheck,
  UserCheck,
  UserX,
} from 'lucide-react';
import { DataTableColumnHeader, InlineSkeleton } from '@/components/core';
import { PersonAvatar } from '@/components/core/person-avatar/person-avatar';
import {
  PRESENCE_TEXT_TONE,
  PresenceSwatch,
} from '@/components/core/presence-avatar/presence-avatar';
import { EMPLOYMENT_TYPES } from '@/constant/hr/employment-types';
import { isWorkingFromHome } from '../../schemas/work-status';
import { SkillChips } from '@/components/core/skill-chips/skill-chips';
import type { EmployeeListLookups } from '../../hooks/use-employee-list-lookups';
import { Button } from '@/components/ui-kit/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui-kit/tooltip';
import { useTableDensity } from '@/hooks/use-table-density';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui-kit/popover';
import { cn } from '@/lib/utils';
import {
  PRESENCE_LABEL_KEY,
  PRESENCE_ORDER,
  presenceText,
  PresenceView,
} from '../../utils/employee-presence';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui-kit/dropdown-menu';
import { Employee } from '../../types/employee.types';
import { employeeName } from '../../schemas/display-name';

/**
 * The work from home switch. The column is only passed in for someone holding
 * `DIRECTORY.CHANGE_WFH`, so nobody else sees a control they cannot use.
 */
interface WorkStatusColumnsProps {
  /** Id of the employee whose change is being saved right now, if any. */
  savingEmployeeId?: string;
  onWfhChange: (employee: Employee, next: boolean) => void;
  onOfficeDaysEdit: (employee: Employee) => void;
}

interface CreateEmployeeTableColumnsProps {
  t: (key: string) => string;
  /** Deactivates an active employee or reactivates an inactive one; leave out to hide the action. */
  onToggleActive?: (employee: Employee) => void;
  /** The signed-in person's Blocks user id: nobody can deactivate their own login. */
  currentUserId?: string;
  workStatus?: WorkStatusColumnsProps;
  /** Opens the profile (View profile). */
  onView?: (employee: Employee) => void;
  /** Opens the edit page (Edit); leave out for those who may not update. */
  onEdit?: (employee: Employee) => void;
  /** Copies the employee's email to the clipboard; leave out to hide the copy action. */
  onCopyEmail?: (employee: Employee) => void;
  /** Copies the employee's SLS id to the clipboard; leave out to hide the copy action. */
  onCopyEmployeeId?: (employee: Employee) => void;
  /** Opens the "add fine" dialog for the employee (legacy `HrFineComponent`); leave out to hide
   * the action — gated by `DIRECTORY.CREATE_FINE` at the call site. */
  onFine?: (employee: Employee) => void;
  /** Opens the "assign role" dialog for the employee; leave out to hide the action — gated by
   * `DIRECTORY.ASSIGN_ROLE` at the call site. */
  onAssignRole?: (employee: Employee) => void;
  /** The departments the user may see (`useDepartmentScope().departments`), for the Department
   * column's filter; leave out for no filter. */
  departments?: readonly { ItemId: string; Title: string }[];
  /** Today's presence per user id (People › Employees, for viewers who may read everyone's
   * attendance): adds the presence to each avatar and the Status column. Leave out to show neither. */
  presence?: EmployeePresenceColumnProps;
  /** Names for the Skills, Category, Sub-Category and Employee Type columns (the list's lookups). */
  lookups?: EmployeeListLookups;
}

interface EmployeePresenceColumnProps {
  byUserId: Record<string, PresenceView>;
  isLoading: boolean;
}

type Translate = (key: string, values?: Record<string, string>) => string;

/**
 * The shared person avatar (prototype `av`, with the presence ring and dot) beside the name and, under
 * it, the designation (prototype `personCell`). The avatar follows the row density, as `--av-row`.
 */
const EmployeeNameCell = ({
  employee,
  t,
  view,
  isPresenceLoading,
}: {
  employee: Employee;
  t: Translate;
  view?: PresenceView;
  isPresenceLoading?: boolean;
}) => {
  const [density] = useTableDensity();
  const name = employeeName(employee);
  return (
    <span
      className="flex min-w-0 items-center gap-2 md:min-w-[260px]"
      // The full name and designation when the cell cuts them off.
      title={[name, employee.Designation].filter(Boolean).join(' · ')}
    >
      <PersonAvatar
        name={name}
        size={density === 'condensed' ? 24 : 32}
        presence={!isPresenceLoading && view ? view.presence : undefined}
        presenceLabel={
          view ? `${presenceText(t, view.label)} · ${presenceText(t, view.detail)}` : undefined
        }
        className="max-md:hidden"
      />
      <span className="grid min-w-0">
        <span className="truncate font-medium leading-[17px]">{name}</span>
        {/* Card: the designation is its own line there. */}
        {employee.Designation && (
          <span className="truncate text-xs leading-4 text-muted-foreground max-md:hidden">
            {employee.Designation}
          </span>
        )}
      </span>
    </span>
  );
};

/** The status key (prototype `statusKey`): what each presence's swatch means. */
const StatusKey = ({ t }: { t: Translate }) => (
  <Popover>
    <PopoverTrigger asChild>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        className="size-6 text-muted-foreground"
        aria-label={t('STATUS_KEY')}
        title={t('STATUS_KEY')}
        onClick={(event) => event.stopPropagation()}
      >
        <Info className="size-4" />
      </Button>
    </PopoverTrigger>
    <PopoverContent align="start" className="w-64" onClick={(event) => event.stopPropagation()}>
      <p className="mb-2 text-sm font-semibold">{t('STATUS_KEY')}</p>
      <ul className="grid gap-2">
        {/* "Shift not started" waits for shift times (TODO(shift-config)), so it isn't listed yet. */}
        {PRESENCE_ORDER.filter((presence) => presence !== 'notstarted').map((presence) => (
          <li key={presence} className="flex items-center gap-2 text-sm">
            <PresenceSwatch presence={presence} />
            <span>{t(PRESENCE_LABEL_KEY[presence])}</span>
          </li>
        ))}
      </ul>
    </PopoverContent>
  </Popover>
);

/** Status: today's presence (prototype `presenceText`), the detail as the tip. */
const presenceColumn = (t: Translate, props: EmployeePresenceColumnProps): ColumnDef<Employee> => ({
  id: 'Presence',
  // Card: its own line under the name.
  meta: { mobile: 'body', mobileOrder: 0.5 },
  // The list is paged on the server and presence isn't stored on the profile, so no sorting.
  enableSorting: false,
  header: () => (
    <span className="inline-flex items-center gap-1">
      <span className="text-[13px] font-medium">{t('STATUS')}</span>
      <StatusKey t={t} />
    </span>
  ),
  accessorFn: (row) => props.byUserId[row.UserId ?? '']?.presence ?? '',
  cell: ({ row }) => {
    if (props.isLoading) return <InlineSkeleton className="w-20" />;
    const view = props.byUserId[row.original.UserId ?? ''];
    if (!view) return <span className="text-muted-foreground">—</span>;
    return (
      <span
        className={cn('truncate font-medium', PRESENCE_TEXT_TONE[view.presence])}
        title={presenceText(t, view.detail)}
      >
        {presenceText(t, view.label)}
      </span>
    );
  },
});

/**
 * The columns that start hidden (prototype `colsBtn` hiddenByDefault): the rest of the profile, shown
 * from the Columns menu.
 */
export const EMPLOYEE_HIDDEN_COLUMNS = {
  EmploymentType: false,
  CategoryId: false,
  SubCategory: false,
  PhoneNumber: false,
};

/** The columns the Columns menu offers (prototype `colsBtn`); the name and actions always show. */
export const employeeOptionalColumns = (t: Translate, hasStatus: boolean) => [
  ...(hasStatus ? [{ id: 'Presence', label: t('STATUS') }] : []),
  { id: 'EmployeeID', label: t('EMPLOYEE_ID') },
  { id: 'DepartmentName', label: t('DEPARTMENT') },
  { id: 'Email', label: t('EMAIL') },
  { id: 'EmploymentType', label: t('EMPLOYEE_TYPE') },
  { id: 'Skills', label: t('SKILLS') },
  { id: 'CategoryId', label: t('CATEGORY') },
  { id: 'SubCategory', label: t('SUB_CATEGORY') },
  { id: 'PhoneNumber', label: t('MOBILE_NUMBER') },
];

/**
 * Employee Type, Skills, Category and Sub-Category. Their values are read per page, so there is no sort;
 * the header filters are turned into gateway filters by `useEmployeeColumnWhere`.
 */
const lookupColumns = (t: Translate, lookups: EmployeeListLookups): ColumnDef<Employee>[] => [
  {
    id: 'EmploymentType',
    meta: {
      mobile: 'body',
      mobileOrder: 5,
      filter: {
        type: 'options',
        dropdown: true,
        options: EMPLOYMENT_TYPES.map((type) => ({ value: type, label: type })),
      },
    },
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('EMPLOYEE_TYPE')} />,
    accessorFn: (row) => lookups.employmentTypeOf(row),
    cell: ({ row }) => lookups.employmentTypeOf(row.original) || '—',
  },
  {
    id: 'Skills',
    meta: { mobile: 'full', mobileOrder: 3.5, filter: { type: 'text' } },
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('SKILLS')} />,
    accessorFn: (row) =>
      lookups
        .skillsOf(row)
        .map((skill) => skill.name)
        .join(', '),
    cell: ({ row }) => <SkillChips skills={lookups.skillsOf(row.original)} />,
  },
  {
    // `CategoryId` is the profile's field, so the filter and the id match.
    id: 'CategoryId',
    meta: {
      mobile: 'body',
      mobileOrder: 5,
      filter: { type: 'options', dropdown: true, options: lookups.categoryOptions },
    },
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('CATEGORY')} />,
    accessorFn: (row) => lookups.categoryOf(row),
    cell: ({ row }) => lookups.categoryOf(row.original) || '—',
  },
  {
    id: 'SubCategory',
    meta: {
      mobile: 'body',
      mobileOrder: 5,
      filter: { type: 'options', dropdown: true, options: lookups.subCategoryOptions },
    },
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('SUB_CATEGORY')} />,
    accessorFn: (row) => lookups.subCategoriesOf(row),
    cell: ({ row }) => lookups.subCategoriesOf(row.original) || '—',
  },
];
export const createEmployeeTableColumns = ({
  t,
  onToggleActive,
  currentUserId,
  onView,
  onEdit,
  workStatus,
  onCopyEmail,
  onCopyEmployeeId,
  onFine,
  onAssignRole,
  departments,
  presence,
  lookups,
}: CreateEmployeeTableColumnsProps): ColumnDef<Employee>[] => [
  {
    // Card (as the old portal's employee card): the name as the title, the actions menu top right,
    // then Employee ID | Email, Department | Designation, and WFH when shown.
    id: 'DisplayName',
    // Wide enough for most names and designations to fit on their two lines.
    size: 330,
    meta: { filter: { type: 'text' }, mobile: 'title' },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('NAME')} />,
    accessorFn: (row) => employeeName(row),
    // The avatar (with today's presence for those who may see it) and the name; the email has its own
    // column.
    cell: ({ row }) => (
      <EmployeeNameCell
        employee={row.original}
        t={t}
        view={presence?.byUserId[row.original.UserId ?? '']}
        isPresenceLoading={presence?.isLoading}
      />
    ),
  },
  // Status sits right after the name, as in the prototype.
  ...(presence ? [presenceColumn(t, presence)] : []),
  {
    id: 'EmployeeID',
    meta: { filter: { type: 'text' }, mobile: 'body', mobileOrder: 0 },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('EMPLOYEE_ID')} />,
    accessorFn: (row) => row.EmployeeID ?? '',
    cell: ({ row }) => {
      const employeeId = row.original.EmployeeID;
      if (!employeeId) return null;
      return (
        <div className="flex items-center gap-1">
          {/* Prototype `.mono`; card: wrapped in full rather than cut off. */}
          <span className="truncate font-mono text-[13px] tabular-nums max-md:whitespace-normal max-md:break-all">
            {employeeId}
          </span>
          {onCopyEmployeeId && (
            <Button
              variant="ghost"
              size="icon"
              aria-label={t('COPY_EMPLOYEE_ID')}
              title={t('COPY_EMPLOYEE_ID')}
              className="size-6 shrink-0"
              onClick={(event) => {
                // The row itself opens the employee's profile.
                event.stopPropagation();
                onCopyEmployeeId(row.original);
              }}
            >
              <Copy className="size-4" />
            </Button>
          )}
        </div>
      );
    },
  },
  {
    // Sorted and filtered by the department name stored on the profile; the filter is a dropdown of
    // the departments the user may see (the list's department scope still applies on top).
    id: 'DepartmentName',
    meta: {
      mobile: 'body',
      mobileOrder: 2,
      ...(departments
        ? {
            filter: {
              type: 'options' as const,
              dropdown: true,
              options: departments.map((department) => ({
                value: department.Title,
                label: department.Title,
              })),
            },
          }
        : {}),
    },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('DEPARTMENT')} />,
    accessorFn: (row) => row.DepartmentName ?? '',
  },
  {
    id: 'Designation',
    // The table shows it under the name (prototype `personCell`); this column is the phone card's line,
    // beside the department, as the old portal.
    meta: { cardOnly: true, mobile: 'body', mobileOrder: 3 },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('DESIGNATION')} />,
    accessorFn: (row) => row.Designation ?? '',
  },
  {
    id: 'Email',
    meta: { filter: { type: 'text' }, mobile: 'body', mobileOrder: 1 },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('EMAIL')} />,
    accessorFn: (row) => row.Email ?? '',
    cell: ({ row }) => {
      const email = row.original.Email;
      if (!email) return null;
      return (
        <div className="flex items-center gap-1">
          {/* Card: wrapped in full rather than cut off. */}
          <span className="truncate max-md:whitespace-normal max-md:break-all">{email}</span>
          {onCopyEmail && (
            <Button
              variant="ghost"
              size="icon"
              aria-label={t('COPY_EMAIL')}
              title={t('COPY_EMAIL')}
              className="size-6 shrink-0"
              onClick={(event) => {
                // The row itself opens the employee's profile.
                event.stopPropagation();
                onCopyEmail(row.original);
              }}
            >
              <Copy className="size-4" />
            </Button>
          )}
        </div>
      );
    },
  },
  ...(lookups ? lookupColumns(t, lookups) : []),
  {
    // Optional (hidden by default): the profile's mobile number.
    id: 'PhoneNumber',
    meta: { filter: { type: 'text' }, mobile: 'body', mobileOrder: 5 },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('MOBILE_NUMBER')} />,
    accessorFn: (row) => row.PhoneNumber ?? '',
    cell: ({ row }) => (
      <span className="whitespace-nowrap tabular-nums">{row.original.PhoneNumber}</span>
    ),
  },

  {
    // One ? menu (prototype `rowMenu`), the card's top right on a phone.
    id: 'actions',
    meta: { mobile: 'badge' as const },
    header: () => <span className="sr-only">{t('ACTION')}</span>,
    enableSorting: false,
    enableHiding: false,
    cell: ({ row }) => (
      <EmployeeActions
        employee={row.original}
        t={t}
        currentUserId={currentUserId}
        onView={onView}
        onEdit={onEdit}
        onAssignRole={onAssignRole}
        onFine={onFine}
        onToggleActive={onToggleActive}
        workStatus={workStatus}
      />
    ),
  } as ColumnDef<Employee>,
];

/**
 * A menu item's reason for being disabled, as a tooltip. A disabled item takes no pointer events, so the
 * tip hangs on a wrapper (a native `title` would never show).
 */
const ItemTip = ({ tip, children }: { tip?: string; children: React.ReactNode }) =>
  tip ? (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div>{children}</div>
        </TooltipTrigger>
        <TooltipContent side="left">{tip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : (
    <>{children}</>
  );
interface EmployeeActionsProps extends Pick<
  CreateEmployeeTableColumnsProps,
  | 't'
  | 'currentUserId'
  | 'onView'
  | 'onEdit'
  | 'onAssignRole'
  | 'onFine'
  | 'onToggleActive'
  | 'workStatus'
> {
  employee: Employee;
}

/**
 * The row's ? menu (prototype `empMenu`): View profile, Edit, then Assign role and Add fine, then
 * Deactivate / Reactivate. Each shows only when its handler is given; you cannot change your own roles or
 * deactivate yourself.
 */
export const EmployeeActions = ({
  employee,
  t,
  currentUserId,
  onView,
  onEdit,
  onAssignRole,
  onFine,
  onToggleActive,
  workStatus,
}: EmployeeActionsProps) => {
  const isInactive = employee.IsActive === false;
  const isOwnAccount = Boolean(currentUserId) && employee.UserId === currentUserId;
  const isSelf = !isInactive && isOwnAccount;
  const hasMiddle = Boolean(onAssignRole || onFine || workStatus);
  const isWfh = isWorkingFromHome(employee);
  const isSavingWork = workStatus?.savingEmployeeId === employee.ItemId;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={t('ACTION')}
          title={t('ACTION')}
          onClick={(event) => event.stopPropagation()}
        >
          <Ellipsis className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      {/* React bubbles portal events to the row, so without this a pick also opens the profile. */}
      <DropdownMenuContent
        align="end"
        className="max-sm:w-auto max-sm:min-w-48 max-sm:max-w-[calc(100vw-16px)] [&_[role=menuitem]]:cursor-pointer [&_[role=menuitem][data-disabled]]:cursor-not-allowed"
        onClick={(event) => event.stopPropagation()}
      >
        {onView && (
          <DropdownMenuItem onSelect={() => onView(employee)}>
            <Eye className="size-4" />
            {t('VIEW_PROFILE')}
          </DropdownMenuItem>
        )}
        {onEdit && (
          <DropdownMenuItem onSelect={() => onEdit(employee)}>
            <Pencil className="size-4" />
            {t('EDIT')}
          </DropdownMenuItem>
        )}
        {hasMiddle && (onView || onEdit) && <DropdownMenuSeparator />}
        {onAssignRole && (
          <ItemTip tip={isOwnAccount ? t('CANNOT_CHANGE_OWN_ROLES') : undefined}>
            <DropdownMenuItem disabled={isOwnAccount} onSelect={() => onAssignRole(employee)}>
              <ShieldCheck className="size-4" />
              {t('ASSIGN_ROLE')}
            </DropdownMenuItem>
          </ItemTip>
        )}
        {onFine && (
          <DropdownMenuItem onSelect={() => onFine(employee)}>
            <Receipt className="size-4" />
            {t('FINE')}
          </DropdownMenuItem>
        )}
        {workStatus && (
          <>
            <DropdownMenuItem
              disabled={isSavingWork}
              onSelect={() => workStatus.onWfhChange(employee, !isWfh)}
            >
              <Laptop className="size-4" />
              {isWfh ? t('TURN_OFF_WFH') : t('TURN_ON_WFH')}
            </DropdownMenuItem>
            <ItemTip tip={isWfh ? t('TURN_OFF_WFH_FIRST') : undefined}>
              <DropdownMenuItem
                disabled={isWfh || isSavingWork}
                onSelect={() => workStatus.onOfficeDaysEdit(employee)}
              >
                <Building2 className="size-4" />
                {t('WORK_FROM_OFFICE_DAYS')}
              </DropdownMenuItem>
            </ItemTip>
          </>
        )}{' '}
        {onToggleActive && (
          <>
            {(hasMiddle || onView || onEdit) && <DropdownMenuSeparator />}
            <ItemTip tip={isSelf ? t('CANNOT_DEACTIVATE_YOURSELF') : undefined}>
              <DropdownMenuItem
                disabled={isSelf}
                className={isInactive ? undefined : 'text-destructive focus:text-destructive'}
                onSelect={() => onToggleActive(employee)}
              >
                {isInactive ? <UserCheck className="size-4" /> : <UserX className="size-4" />}
                {isInactive ? t('REACTIVATE_USER') : t('DEACTIVATE_USER')}
              </DropdownMenuItem>
            </ItemTip>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
